/**
 * RBAC Hooks - Workspace-Based System
 * 
 * React hooks for permission checking and role-based UI rendering
 * Now supports Platform vs Workspace role separation
 */

import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from 'react';
import { api } from '../services/api';
import { 
  PlatformRole,
  WorkspaceRole, 
  Permission, 
  UserPermissions,
  Workspace,
  WorkspaceMember,
  WorkspaceInvitation,
  WORKSPACE_ROLE_HIERARCHY,
  WORKSPACE_ROLE_DISPLAY_NAMES,
  PLATFORM_ROLE_DISPLAY_NAMES,
} from '../types/rbac';

// ==================== CONTEXT ====================

interface RBACContextValue {
  // Platform context
  platformRole: PlatformRole;
  isPlatformAdmin: boolean;
  
  // Workspace context
  currentWorkspace?: Workspace;
  workspaceRole?: WorkspaceRole;
  workspaces: Workspace[];
  
  // Permissions
  permissions: Permission[];
  teamIds: string[];
  
  // Loading state
  loading: boolean;
  error: string | null;
  
  // Permission checks
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  isRoleOrHigher: (role: WorkspaceRole) => boolean;
  
  // Item-level checks
  canEdit: (itemOwnerId?: string) => boolean;
  canDelete: (itemOwnerId?: string) => boolean;
  
  // Workspace management
  setCurrentWorkspace: (workspaceId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const RBACContext = createContext<RBACContextValue | null>(null);

const getCachedWorkspaceKey = (userId?: string | null) =>
  `elvoaq_cached_workspaces_${userId || 'anonymous'}`;

const getStoredUserId = (): string | null => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.id || null;
  } catch {
    return null;
  }
};

const readCachedWorkspaces = (userId?: string | null): Workspace[] => {
  try {
    const raw = localStorage.getItem(getCachedWorkspaceKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeCachedWorkspaces = (userId: string, value: Workspace[]) => {
  try {
    localStorage.setItem(getCachedWorkspaceKey(userId), JSON.stringify(value));
  } catch {
    // best effort cache only
  }
};

// ==================== PROVIDER ====================

interface RBACProviderProps {
  children: ReactNode;
}

export function RBACProvider({ children }: RBACProviderProps) {
  // Platform state
  const [platformRole, setPlatformRole] = useState<PlatformRole>('PLATFORM_USER');
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  
  // Workspace state
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [currentWorkspace, setCurrentWorkspaceState] = useState<Workspace | undefined>();
  const [workspaceRole, setWorkspaceRole] = useState<WorkspaceRole | undefined>();
  
  // Permissions
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [teamIds, setTeamIds] = useState<string[]>([]);
  
  // Loading state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch user context and workspaces (with retry on failure)
  const fetchUserContext = useCallback(async (retryCount = 0) => {
    const MAX_RETRIES = 2;
    try {
      setLoading(true);
      setError(null);

      const storedUserId = getStoredUserId();
      const cachedWorkspaces = readCachedWorkspaces(storedUserId);
      
      // Fetch user's workspaces - API returns { success, workspaces }
      const workspacesResponse = await api.get('/workspaces');
      const workspacesList: Workspace[] = workspacesResponse.data?.workspaces || workspacesResponse.data || [];

      // Safety check: if the API returned empty but user previously had workspaces,
      // retry before accepting the empty result (protects against deploy-time transient failures).
      const storedWorkspaceId = localStorage.getItem('currentWorkspaceId');
      if (
        workspacesList.length === 0 &&
        (storedWorkspaceId || cachedWorkspaces.length > 0) &&
        retryCount < MAX_RETRIES
      ) {
        console.warn(`Workspace fetch returned empty but stored workspace exists — retrying (${retryCount + 1}/${MAX_RETRIES})...`);
        await new Promise(r => setTimeout(r, 1500 * (retryCount + 1)));
        return fetchUserContext(retryCount + 1);
      }

      const effectiveWorkspaces = workspacesList.length > 0 ? workspacesList : cachedWorkspaces;
      setWorkspaces(effectiveWorkspaces);

      if (workspacesList.length > 0 && storedUserId) {
        writeCachedWorkspaces(storedUserId, workspacesList);
      }

      if (workspacesList.length === 0 && cachedWorkspaces.length > 0) {
        setError('Using last known workspaces while the server sync catches up.');
      }
      
      // Get stored workspace preference (also used above for retry guard)
      // Determine current workspace
      let activeWorkspace: Workspace | undefined;
      if (storedWorkspaceId) {
        activeWorkspace = effectiveWorkspaces.find(w => w.id === storedWorkspaceId);
      }
      if (!activeWorkspace && effectiveWorkspaces.length > 0) {
        activeWorkspace = effectiveWorkspaces[0];
      }
      
      if (activeWorkspace) {
        setCurrentWorkspaceState(activeWorkspace);
        setWorkspaceRole(activeWorkspace.role as WorkspaceRole);
        localStorage.setItem('currentWorkspaceId', activeWorkspace.id);
        
        // Fetch permissions for this workspace — use a separate try/catch so that a
        // permissions failure (e.g. after a plan change) does NOT wipe workspace data.
        try {
          const permResponse = await api.get<UserPermissions>(`/workspaces/${activeWorkspace.id}/permissions`);
          setPermissions(permResponse.data.permissions || []);
          setPlatformRole(permResponse.data.platformRole || 'PLATFORM_USER');
          setIsPlatformAdmin(permResponse.data.isPlatformAdmin || false);
          setTeamIds(permResponse.data.teamIds || []);
        } catch (permErr: any) {
          // Permissions endpoint failed (e.g. plan just changed / subscription update in flight).
          // Keep workspaces intact — only reset permission-specific state.
          console.warn('Failed to fetch workspace permissions, using defaults:', permErr);
          setPermissions([]);
          setTeamIds([]);
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            try {
              const user = JSON.parse(storedUser);
              setPlatformRole(user.platformRole || 'PLATFORM_USER');
              setIsPlatformAdmin(user.platformRole === 'PLATFORM_ADMIN');
            } catch {}
          }
        }
      } else {
        // No workspace - user just signed up
        setCurrentWorkspaceState(undefined);
        setWorkspaceRole(undefined);
        setPermissions([]);
        
        // Still fetch basic user info
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const user = JSON.parse(storedUser);
            setPlatformRole(user.platformRole || 'PLATFORM_USER');
            setIsPlatformAdmin(user.platformRole === 'PLATFORM_ADMIN');
          } catch {}
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch user context:', err);

      const storedUserId = getStoredUserId();
      const cachedWorkspaces = readCachedWorkspaces(storedUserId);

      // Retry on transient errors (e.g. deploy cold start)
      if (retryCount < MAX_RETRIES) {
        console.warn(`Workspace fetch failed — retrying (${retryCount + 1}/${MAX_RETRIES})...`);
        await new Promise(r => setTimeout(r, 1500 * (retryCount + 1)));
        return fetchUserContext(retryCount + 1);
      }

      setError(err.message || 'Failed to load permissions');
      
      // Try to use stored data as fallback.
      // NOTE: Do NOT call setWorkspaces([]) here — any workspaces already loaded
      // before the error (e.g. from a failed permissions sub-request) must be preserved
      // so that pages like UserManagement can still render correctly.
      if (cachedWorkspaces.length > 0) {
        setWorkspaces((prev) => (prev.length > 0 ? prev : cachedWorkspaces));
        setCurrentWorkspaceState((prev) => prev || cachedWorkspaces[0]);
      }

      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          setPlatformRole(user.platformRole || 'PLATFORM_USER');
          setIsPlatformAdmin(user.platformRole === 'PLATFORM_ADMIN');
        } catch {}
      }
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Switch to a different workspace
  const setCurrentWorkspace = useCallback(async (workspaceId: string) => {
    const workspace = workspaces.find(w => w.id === workspaceId);
    if (!workspace) {
      throw new Error('Workspace not found');
    }
    
    setCurrentWorkspaceState(workspace);
    setWorkspaceRole(workspace.role);
    localStorage.setItem('currentWorkspaceId', workspaceId);
    
    // Fetch permissions for the new workspace
    try {
      const permResponse = await api.get<UserPermissions>(`/workspaces/${workspaceId}/permissions`);
      setPermissions(permResponse.data.permissions || []);
      setTeamIds(permResponse.data.teamIds || []);
    } catch (err: any) {
      console.error('Failed to fetch workspace permissions:', err);
      setError(err.message);
    }
  }, [workspaces]);

  useEffect(() => {
    // Only fetch if user is logged in
    const token = localStorage.getItem('token');
    if (token) {
      fetchUserContext();
    } else {
      setLoading(false);
    }
  }, [fetchUserContext]);

  // Check if user has a specific permission
  const hasPermission = useCallback((permission: Permission): boolean => {
    // Platform admins have all permissions
    if (isPlatformAdmin) return true;
    return permissions.includes(permission);
  }, [permissions, isPlatformAdmin]);

  // Check if user has any of the specified permissions
  const hasAnyPermission = useCallback((perms: Permission[]): boolean => {
    if (isPlatformAdmin) return true;
    return perms.some(p => permissions.includes(p));
  }, [permissions, isPlatformAdmin]);

  // Check if user has all of the specified permissions
  const hasAllPermissions = useCallback((perms: Permission[]): boolean => {
    if (isPlatformAdmin) return true;
    return perms.every(p => permissions.includes(p));
  }, [permissions, isPlatformAdmin]);

  // Check if user's workspace role is at or above the specified role
  const isRoleOrHigher = useCallback((role: WorkspaceRole): boolean => {
    if (isPlatformAdmin) return true;
    if (!workspaceRole) return false;
    return WORKSPACE_ROLE_HIERARCHY[workspaceRole] >= WORKSPACE_ROLE_HIERARCHY[role];
  }, [workspaceRole, isPlatformAdmin]);

  // Check if user can edit (own items or has elevated permissions)
  const canEdit = useCallback((itemOwnerId?: string): boolean => {
    const userId = localStorage.getItem('userId');
    
    // Owner can always edit their own items
    if (itemOwnerId && userId === itemOwnerId) {
      return hasPermission(Permission.ITEM_EDIT_OWN);
    }
    
    // Check for broader edit permissions
    return hasAnyPermission([
      Permission.ITEM_EDIT_ALL,
      Permission.ITEM_EDIT_WORKSPACE,
    ]);
  }, [hasPermission, hasAnyPermission]);

  // Check if user can delete (own items or has elevated permissions)
  const canDelete = useCallback((itemOwnerId?: string): boolean => {
    const userId = localStorage.getItem('userId');
    
    // Owner can delete own items
    if (itemOwnerId && userId === itemOwnerId) {
      return hasPermission(Permission.ITEM_DELETE_OWN);
    }
    
    // Check for broader delete permissions
    return hasPermission(Permission.ITEM_DELETE_ALL);
  }, [hasPermission]);

  const value: RBACContextValue = {
    platformRole,
    isPlatformAdmin,
    currentWorkspace,
    workspaceRole,
    workspaces,
    permissions,
    teamIds,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isRoleOrHigher,
    canEdit,
    canDelete,
    setCurrentWorkspace,
    refresh: fetchUserContext,
  };

  return (
    <RBACContext.Provider value={value}>
      {children}
    </RBACContext.Provider>
  );
}

// ==================== HOOKS ====================

/**
 * Main hook to access RBAC context
 */
export function useRBAC() {
  const context = useContext(RBACContext);
  
  if (!context) {
    throw new Error('useRBAC must be used within an RBACProvider');
  }
  
  return context;
}

/**
 * Hook to check a specific permission
 */
export function usePermission(permission: Permission): boolean {
  const { hasPermission, loading } = useRBAC();
  
  if (loading) return false;
  return hasPermission(permission);
}

/**
 * Hook to check multiple permissions (any)
 */
export function useAnyPermission(permissions: Permission[]): boolean {
  const { hasAnyPermission, loading } = useRBAC();
  
  if (loading) return false;
  return hasAnyPermission(permissions);
}

/**
 * Hook to check multiple permissions (all)
 */
export function useAllPermissions(permissions: Permission[]): boolean {
  const { hasAllPermissions, loading } = useRBAC();
  
  if (loading) return false;
  return hasAllPermissions(permissions);
}

/**
 * Hook to check workspace role level
 */
export function useWorkspaceRole(minRole: WorkspaceRole): boolean {
  const { isRoleOrHigher, loading } = useRBAC();
  
  if (loading) return false;
  return isRoleOrHigher(minRole);
}

/**
 * Hook to get current user's role info
 */
export function useUserRole() {
  const { platformRole, workspaceRole, currentWorkspace, isPlatformAdmin, loading } = useRBAC();
  
  return {
    platformRole,
    workspaceRole,
    platformRoleDisplay: PLATFORM_ROLE_DISPLAY_NAMES[platformRole],
    workspaceRoleDisplay: workspaceRole ? WORKSPACE_ROLE_DISPLAY_NAMES[workspaceRole] : undefined,
    currentWorkspace,
    isPlatformAdmin,
    loading,
  };
}

// ==================== WORKSPACE HOOKS ====================

/**
 * Hook to manage workspaces
 */
export function useWorkspaces() {
  const { workspaces, currentWorkspace, setCurrentWorkspace, refresh, loading, error } = useRBAC();
  const [creating, setCreating] = useState(false);

  const createWorkspace = async (data: { 
    name: string; 
    description?: string; 
    type: 'PERSONAL' | 'ORGANIZATION';
    logo?: string;
  }) => {
    try {
      setCreating(true);
      const response = await api.post('/workspaces', data);
      await refresh();
      return response.data;
    } catch (err: any) {
      throw err;
    } finally {
      setCreating(false);
    }
  };

  const updateWorkspace = async (workspaceId: string, data: { 
    name?: string; 
    description?: string; 
    logo?: string;
  }) => {
    const response = await api.patch(`/workspaces/${workspaceId}`, data);
    await refresh();
    return response.data;
  };

  const deleteWorkspace = async (workspaceId: string) => {
    await api.delete(`/workspaces/${workspaceId}`);
    await refresh();
  };

  const convertToOrganization = async (workspaceId: string) => {
    const response = await api.post(`/workspaces/${workspaceId}/convert-to-organization`);
    await refresh();
    return response.data;
  };

  return {
    workspaces,
    currentWorkspace,
    setCurrentWorkspace,
    loading,
    creating,
    error,
    refresh,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    convertToOrganization,
  };
}

/**
 * Hook to manage workspace members
 */
export function useWorkspaceMembers(workspaceId?: string) {
  const { currentWorkspace } = useRBAC();
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const wsId = workspaceId || currentWorkspace?.id;

  const fetchMembers = useCallback(async () => {
    if (!wsId) {
      setMembers([]);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/workspaces/${wsId}/members`);
      // API returns { success, members }
      setMembers(response.data?.members || response.data || []);
    } catch (err: any) {
      console.error('Error fetching members:', err);
      setError(err.message || 'Failed to fetch members');
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [wsId]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const changeRole = async (userId: string, role: WorkspaceRole) => {
    if (!wsId) throw new Error('No workspace selected');
    try {
      await api.patch(`/workspaces/${wsId}/members/${userId}`, { role });
      await fetchMembers();
    } catch (err: any) {
      setError(err.message || 'Failed to change role');
      throw err;
    }
  };

  const removeMember = async (userId: string) => {
    if (!wsId) throw new Error('No workspace selected');
    try {
      await api.delete(`/workspaces/${wsId}/members/${userId}`);
      await fetchMembers();
    } catch (err: any) {
      setError(err.message || 'Failed to remove member');
      throw err;
    }
  };

  const transferOwnership = async (newOwnerId: string) => {
    if (!wsId) throw new Error('No workspace selected');
    const response = await api.post(`/workspaces/${wsId}/transfer-ownership`, { newOwnerId });
    await fetchMembers();
    return response.data;
  };

  return {
    members,
    loading,
    error,
    refresh: fetchMembers,
    changeRole,
    removeMember,
    transferOwnership,
  };
}

/**
 * Hook to manage workspace invitations
 */
export function useWorkspaceInvitations(workspaceId?: string) {
  const { currentWorkspace } = useRBAC();
  const [invitations, setInvitations] = useState<WorkspaceInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const wsId = workspaceId || currentWorkspace?.id;

  const fetchInvitations = useCallback(async () => {
    if (!wsId) {
      setInvitations([]);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/workspaces/${wsId}/invitations`);
      // API returns { success, invitations }
      setInvitations(response.data?.invitations || response.data || []);
    } catch (err: any) {
      console.error('Error fetching invitations:', err);
      setError(err.message || 'Failed to fetch invitations');
      setInvitations([]);
    } finally {
      setLoading(false);
    }
  }, [wsId]);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  const inviteMember = async (email: string, role: WorkspaceRole) => {
    if (!wsId) throw new Error('No workspace selected');
    try {
      const response = await api.post(`/workspaces/${wsId}/invitations`, { email, role });
      await fetchInvitations();
      return response.data;
    } catch (err: any) {
      setError(err.message || 'Failed to invite member');
      throw err;
    }
  };

  const revokeInvitation = async (invitationId: string) => {
    if (!wsId) throw new Error('No workspace selected');
    try {
      await api.delete(`/workspaces/${wsId}/invitations/${invitationId}`);
      await fetchInvitations();
    } catch (err: any) {
      setError(err.message || 'Failed to revoke invitation');
      throw err;
    }
  };

  const resendInvitation = async (invitationId: string) => {
    if (!wsId) throw new Error('No workspace selected');
    const response = await api.post(`/workspaces/${wsId}/invitations/${invitationId}/resend`);
    return response.data;
  };

  return {
    invitations,
    loading,
    error,
    refresh: fetchInvitations,
    inviteMember,
    revokeInvitation,
    resendInvitation,
  };
}

/**
 * Hook to accept a workspace invitation
 */
export function useAcceptInvitation() {
  const { refresh } = useRBAC();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const acceptInvitation = async (token: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.post(`/workspaces/accept-invitation/${token}`);
      await refresh();
      return response.data;
    } catch (err: any) {
      setError(err.message || 'Failed to accept invitation');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    acceptInvitation,
    loading,
    error,
  };
}

// ==================== TEAM HOOKS ====================

/**
 * Hook to manage teams within a workspace
 */
export function useTeams(workspaceId?: string) {
  const { currentWorkspace } = useRBAC();
  const [teams, setTeams] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const wsId = workspaceId || currentWorkspace?.id;

  const fetchTeams = useCallback(async () => {
    if (!wsId) {
      setTeams([]);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/workspaces/${wsId}/teams`);
      setTeams(response.data || []);
    } catch (err: any) {
      console.error('Error fetching teams:', err);
      setError(err.message || 'Failed to fetch teams');
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, [wsId]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const createTeam = async (data: { name: string; description?: string }) => {
    if (!wsId) throw new Error('No workspace selected');
    try {
      const response = await api.post(`/workspaces/${wsId}/teams`, data);
      await fetchTeams();
      return response.data;
    } catch (err: any) {
      setError(err.message || 'Failed to create team');
      throw err;
    }
  };

  return {
    teams,
    loading,
    error,
    refresh: fetchTeams,
    createTeam,
  };
}

// ==================== SHARED LINKS HOOKS ====================

/**
 * Hook to manage shared links
 */
export function useSharedLinks() {
  const [shares, setShares] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchShares = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/rbac/my-shares');
      setShares(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch shares');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchShares();
  }, [fetchShares]);

  const createShare = async (resourceType: string, resourceId: string, options?: any) => {
    const response = await api.post('/rbac/share', { resourceType, resourceId, ...options });
    await fetchShares();
    return response.data;
  };

  const revokeShare = async (shareId: string) => {
    await api.delete(`/rbac/share/${shareId}`);
    await fetchShares();
  };

  return {
    shares,
    loading,
    error,
    refresh: fetchShares,
    createShare,
    revokeShare,
  };
}

// ==================== SUGGESTIONS HOOKS ====================

/**
 * Hook to manage edit suggestions
 */
export function useSuggestions() {
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSuggestions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/rbac/suggestions');
      setSuggestions(response.data || []);
    } catch (err: any) {
      console.error('Error fetching suggestions:', err);
      setError(err.message || 'Failed to fetch suggestions');
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  const createSuggestion = async (data: {
    itemId: string;
    fieldName: string;
    currentValue?: string;
    suggestedValue: string;
    explanation?: string;
  }) => {
    try {
      const response = await api.post('/rbac/suggestions', data);
      return response.data;
    } catch (err: any) {
      setError(err.message || 'Failed to create suggestion');
      throw err;
    }
  };

  const reviewSuggestion = async (suggestionId: string, approved: boolean, reviewNote?: string) => {
    try {
      await api.patch(`/rbac/suggestions/${suggestionId}`, { approved, reviewNote });
      await fetchSuggestions();
    } catch (err: any) {
      setError(err.message || 'Failed to review suggestion');
      throw err;
    }
  };

  return {
    suggestions,
    loading,
    error,
    refresh: fetchSuggestions,
    createSuggestion,
    reviewSuggestion,
  };
}

// ==================== LEGACY COMPATIBILITY ====================

/**
 * @deprecated Use useWorkspaces instead
 */
export function useOrganizations() {
  const { workspaces, loading, error, refresh } = useWorkspaces();
  return {
    organizations: workspaces,
    loading,
    error,
    refresh,
    createOrganization: async (_data: any) => {
      console.warn('useOrganizations is deprecated, use useWorkspaces instead');
      throw new Error('Deprecated: use useWorkspaces().createWorkspace');
    },
  };
}

/**
 * @deprecated Use useWorkspaceMembers instead
 */
export function useOrganizationMembers(organizationId: string) {
  return useWorkspaceMembers(organizationId);
}

// Legacy hook alias
export const useRole = useWorkspaceRole;

const RBACHooks = {
  RBACProvider,
  useRBAC,
  usePermission,
  useAnyPermission,
  useAllPermissions,
  useWorkspaceRole,
  useRole,
  useUserRole,
  useWorkspaces,
  useWorkspaceMembers,
  useWorkspaceInvitations,
  useAcceptInvitation,
  useTeams,
  useSharedLinks,
  useSuggestions,
  // Legacy
  useOrganizations,
  useOrganizationMembers,
};

export default RBACHooks;
